import { api, type Company, type Rec } from '@tnn/app-kit'
import type { RefMap } from '@tnn/core'
import type { LinkSignedSubject, PublicBranch, SelfReceiptInput, SelfReceiptState, Signature, TermsVersion } from '@tnn/domain'

/**
 * The signing site's endpoints, no account: a signing link's document and signing it (`/api/public/sign`), and a bill's
 * payment with the receipt in a company's name its payer issues (`/api/public/track/<code>/receipts`)
 */

/**
 * A document as its signing link shows it (modules/signature/links.ts `linkedDocument`): an accounting document with
 * its customer and invoices, a bill with the shipping terms it keeps in full; `link.role` the line it signs
 */
export interface LinkedDocument {
  link: { status: 'open' | 'signed' | 'expired'; expiresAt: string; role: string }
  subject: LinkSignedSubject
  record: Rec
  refs: RefMap
  customer?: Rec | null
  invoices?: Rec[]
  terms?: TermsVersion | null
  company: Company | null
  signatures: Signature[]
}

export const signApi = {
  /** A document's signing link → the document as it prints (SIGN_LINK_INVALID 404, SIGN_LINK_EXPIRED 410) */
  document: (token: string) => api.get<LinkedDocument>(`/public/sign/${encodeURIComponent(token)}`),
  /** Signing it once: who signs and the signature (a PNG) → the document, signed */
  sign: (token: string, body: { name: string; title: string | null; method: 'drawn' | 'photo'; image: Blob }) => {
    const form = new FormData()
    form.append('name', body.name)
    if (body.title) form.append('title', body.title)
    form.append('method', body.method)
    form.append('file', new File([body.image], 'signature.png', { type: 'image/png' }))
    return api.post<LinkedDocument>(`/public/sign/${encodeURIComponent(token)}`, form)
  },
}

/**
 * A payment of a bill as its page shows it (modules/tms/public-receipts.ts `selfReceiptPage`): how the receipt in a
 * company's name stands for it, where the money was taken, and that receipt — a preview to fill in (no number), or as
 * issued; none when it does not open here
 */
export interface SelfReceiptPage {
  bill: { number: string; code: string }
  state: SelfReceiptState
  /** The last day the payer may issue it here */
  until: string
  paidOn: string
  /** The money it took, all the bills it paid together */
  received: number
  branch: PublicBranch
  /** The receipt in the customer's name standing for the payment */
  number: string | null
  record: Rec | null
  refs: RefMap
  /** The company's details known for the payer, to start from */
  defaults: Partial<SelfReceiptInput> | null
  company: Company | null
}

const receiptPath = (code: string, id: string) => `/public/track/${encodeURIComponent(code)}/receipts/${encodeURIComponent(id)}`

export const receiptApi = {
  /** A payment of the bill whose tracking code it is → its page (404 NOT_FOUND / TRACK_NOT_FOUND) */
  page: (code: string, id: string) => api.get<SelfReceiptPage>(receiptPath(code, id)),
  /** Issuing it in the company's name → the page, issued (409 when it no longer may be, 422 at a field) */
  issue: (code: string, id: string, input: SelfReceiptInput) => api.post<SelfReceiptPage>(receiptPath(code, id), input),
}
